import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const CATEGORIES = ["All", "Oil Education", "Recipes", "Ingredients", "PhudG Stories"];
const OIL_FILTERS = ["All", "Mustard", "Groundnut", "Sunflower"];
const MEAL_FILTERS = ["All", "Breakfast", "Lunch", "Dinner", "Snacks", "Pickles"];

const chipCls = (active) =>
  `rounded-full px-5 py-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
    active
      ? "bg-ink text-cream"
      : "border border-ink/15 text-ink/60 hover:border-gold hover:text-amberbrand"
  }`;

export default function JournalPage() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");
  const [oil, setOil] = useState("All");
  const [meal, setMeal] = useState("All");

  useEffect(() => {
    axios.get(`${API}/journal`).then((res) => setPosts(res.data)).catch(() => {});
  }, []);

  let visible = cat === "All" ? posts : posts.filter((p) => p.category === cat);
  if (cat === "Recipes") {
    if (oil !== "All") visible = visible.filter((p) => p.oil === oil);
    if (meal !== "All") visible = visible.filter((p) => (p.meals || []).includes(meal));
  }

  const pickCat = (c) => {
    setCat(c);
    setOil("All");
    setMeal("All");
  };

  return (
    <main data-testid="journal-page">
      <Seo title={"Cooking Oil Tips and Guides | Cold-Pressed Oil Journal | PhudG"} description={"Learn how to choose, use and store cooking oils with practical guides on mustard, groundnut and sunflower oils for Indian cooking."} image="/images/seeds.jpg" path="/journal" />
      <Header />
      <section className="bg-ink grain relative pt-36 pb-20 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">The Journal</p>
          <h1 className="font-serif text-5xl md:text-7xl text-cream tracking-tighter">
            Oil, <span className="italic text-gold">understood.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base font-light text-cream/60 leading-relaxed">
            Oil education, recipes and ingredient notes — written plainly, so
            you know exactly what you're cooking with.
          </p>
        </div>
      </section>

      <section className="bg-cream py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-3 mb-6">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`journal-filter-${c.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => pickCat(c)}
                className={chipCls(cat === c)}
              >
                {c}
              </button>
            ))}
          </div>

          {cat === "Recipes" && (
            <div data-testid="cook-with-phudg-filters" className="mb-14 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-ink/40 w-20">Oil</span>
                {OIL_FILTERS.map((o) => (
                  <button key={o} data-testid={`oil-filter-${o.toLowerCase()}`} onClick={() => setOil(o)} className={chipCls(oil === o)}>
                    {o}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-ink/40 w-20">Meal</span>
                {MEAL_FILTERS.map((m) => (
                  <button key={m} data-testid={`meal-filter-${m.toLowerCase()}`} onClick={() => setMeal(m)} className={chipCls(meal === m)}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          {cat !== "Recipes" && <div className="mb-8" />}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {visible.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              >
                <Link to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="group block">
                  <div className="overflow-hidden rounded-3xl border border-ink/10 mb-6">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-amberbrand">{p.category}</span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-ink/35">{p.read_time}</span>
                  </div>
                  <h2 className="font-serif text-2xl text-ink group-hover:text-amberbrand transition-colors duration-300 mb-3 flex items-start gap-2">
                    {p.title}
                    <ArrowUpRight size={18} className="mt-1 shrink-0 text-ink/25 group-hover:text-gold transition-colors duration-300" />
                  </h2>
                  <p className="text-sm font-light text-ink/55 leading-relaxed">{p.excerpt}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
